import { useState } from "react";
import { Navbar } from "../components/Navbar";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, MessageCircle, Clock, Loader2 } from "lucide-react";

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate sending
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success("Messaggio inviato! Ti risponderemo al più presto.");
    setFormData({ name: "", email: "", subject: "", message: "" });
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-slate-900">Contattaci</h1>
          <p className="text-slate-500 mt-2">Siamo qui per aiutarti</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border p-6">
              <h2 className="font-bold text-slate-900 mb-4">Informazioni di Contatto</h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#004E64]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#004E64]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Email</p>
                    <a href="mailto:info@lavoroticino.ch" className="text-sm text-[#004E64] hover:underline">
                      info@lavoroticino.ch
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Telefono / WhatsApp</p>
                    <a href="tel:+41786141854" className="text-sm text-[#004E64] hover:underline">
                      +41 78 614 18 54
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Indirizzo</p>
                    <p className="text-sm text-slate-600">6945 Origlio, Ticino, Svizzera</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Orari</p>
                    <p className="text-sm text-slate-600">Lun-Ven: 9:00-18:00</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#004E64] text-white rounded-xl p-6">
              <MessageCircle className="w-8 h-8 mb-3" />
              <h3 className="font-bold mb-2">Hai bisogno di aiuto rapido?</h3>
              <p className="text-sm text-white/80 mb-4">
                Consulta la nostra sezione FAQ per trovare risposte immediate.
              </p>
              <Link to="/help">
                <Button variant="secondary" size="sm" className="w-full">
                  Vai alla Guida
                </Button>
              </Link>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border p-6 sm:p-8">
              <h2 className="font-bold text-slate-900 mb-6">Inviaci un messaggio</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Nome *</Label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Il tuo nome"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="La tua email"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Oggetto *</Label>
                  <Input
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Di cosa hai bisogno?"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Messaggio *</Label>
                  <Textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Scrivi il tuo messaggio..."
                    rows={5}
                    className="mt-1"
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-[#DA291C] hover:bg-[#B91C1C]">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Invia messaggio
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
